'use client';

import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import ChangelogContent from './ChangelogContent';

interface GitHubRelease {
  id: number;
  tag_name: string;
  name: string;
  body: string;
  published_at: string;
  html_url: string;
  prerelease: boolean;
}

interface ChangelogPaginatedProps {
  releases: GitHubRelease[];
  releasesPerPage: number;
}

// Pagination is read from the ?page= query string client-side. The releases
// array is baked at build time and passed in full from the server component.
// This keeps URLs identical to the previous server-rendered behavior
// (/docs/changelog?page=N) while remaining compatible with static export.
export default function ChangelogPaginated({
  releases,
  releasesPerPage,
}: ChangelogPaginatedProps) {
  const searchParams = useSearchParams();
  const totalPages = Math.max(1, Math.ceil(releases.length / releasesPerPage));
  const requestedPage = Number(searchParams.get('page')) || 1;
  const currentPage = Math.max(1, Math.min(requestedPage, totalPages));
  const startIndex = (currentPage - 1) * releasesPerPage;
  const currentReleases = releases.slice(startIndex, startIndex + releasesPerPage);

  return (
    <>
      <ChangelogContent releases={currentReleases} />

      {totalPages > 1 && (
        <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-gray-200 pt-8">
          <div className="text-sm text-gray-600">
            Showing {startIndex + 1}-{Math.min(startIndex + releasesPerPage, releases.length)} of {releases.length} releases
          </div>
          <div className="flex items-center gap-2">
            <Link
              href={currentPage > 1 ? `/docs/changelog?page=${currentPage - 1}` : '#'}
              className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                currentPage === 1
                  ? 'bg-gray-100 text-gray-400 border-gray-200 pointer-events-none'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400'
              }`}
              aria-disabled={currentPage === 1}
            >
              Previous
            </Link>
            <div className="hidden sm:flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <Link
                  key={page}
                  href={`/docs/changelog?page=${page}`}
                  className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                    currentPage === page
                      ? 'bg-lime-400 text-gray-900 border-lime-500'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                  }`}
                >
                  {page}
                </Link>
              ))}
            </div>
            <div className="sm:hidden px-4 py-2 text-sm font-medium text-gray-700">
              {currentPage} / {totalPages}
            </div>
            <Link
              href={currentPage < totalPages ? `/docs/changelog?page=${currentPage + 1}` : '#'}
              className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                currentPage === totalPages
                  ? 'bg-gray-100 text-gray-400 border-gray-200 pointer-events-none'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400'
              }`}
              aria-disabled={currentPage === totalPages}
            >
              Next
            </Link>
          </div>
        </div>
      )}
    </>
  );
}
